import { useCallback, useState } from 'react'
import type { CreateSchedulePayload, Schedule, ScheduleDetail, UpdateSchedulePayload } from 'types/schedules'
import type { MutationResult, QueryResult } from 'utils/api'
import { qs } from 'utils/api'

import { useApiQuery } from '../useApiQuery'
import { useFetchApi } from '../useFetchApi'

export interface SchedulesParams {
  skip?: number
  take?: number
  scheduledCommunicationId?: string
  includeInactive?: boolean
}

export function useSchedules(params?: SchedulesParams): QueryResult<Schedule[]> {
  return useApiQuery<Schedule[]>(`/api/schedules${qs(params)}`)
}

export function useSchedule(id: string | undefined): QueryResult<ScheduleDetail> {
  return useApiQuery<ScheduleDetail>(id ? `/api/schedules/${id}` : null)
}

export function useCreateSchedule(): MutationResult<Schedule, [CreateSchedulePayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (payload: CreateSchedulePayload): Promise<Schedule> => {
      setLoading(true)
      try {
        return await fetchApi<Schedule>('/api/schedules', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useUpdateSchedule(): MutationResult<Schedule, [string, UpdateSchedulePayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string, payload: UpdateSchedulePayload): Promise<Schedule> => {
      setLoading(true)
      try {
        return await fetchApi<Schedule>(`/api/schedules/${id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useDeleteSchedule(): MutationResult<Schedule, [string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string): Promise<Schedule> => {
      setLoading(true)
      try {
        return await fetchApi<Schedule>(`/api/schedules/${id}`, { method: 'DELETE' })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}
